import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/apiClient.js';
import { toast } from '../../components/index.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { getNavItem } from '../../app/navRegistry.js';
import { NAV_PREFERENCES_CHANGED_EVENT } from '../../app/SideNav.jsx';
import {
  IconSparkles,
  IconClose,
  IconCheck,
  IconChevron,
  IconTarget,
} from '../../components/icons.jsx';
import {
  decodeNavPreferences,
  applyRbacToLayout,
  resolveRailModel,
  deriveRecommendedLayout,
  deriveAvailablePages,
  addPage,
  removePage,
  reorderSectionItem,
  reorderPageWithinSubmenu,
  moveSection,
  selectAll,
  clearOptional,
  withBumpedRevision,
  encodeNavPreferences,
  readRevision,
  isProtectedRoute,
  isCustomLayout,
  createSection,
  renameSection,
  deleteSection,
  setSectionEmoji,
  toggleSectionCollapsed,
  createSubmenu,
  renameSubmenu,
  deleteSubmenu,
  setSubmenuEmoji,
  toggleSubmenuCollapsed,
  movePage,
  validateSectionLabel,
  validateSubmenuLabel,
} from '../../lib/navPreferences.js';
import { NAV_EMOJI_ROWS } from './navEmojiCatalog.js';
import './navMenuEditor.css';

const HOME_SECTION_SOURCE = 'Home';
// Mirrors the API navigation schema caps; client code must not import server modules.
const MAX_NAV_SECTIONS = 24;
const MAX_NAV_SECTION_ITEMS = 64;

function isNavigationRevisionConflict(error) {
  return error?.code === 'REVISION_CONFLICT';
}

function broadcastNavChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NAV_PREFERENCES_CHANGED_EVENT));
  }
}

/**
 * Generic keyboard + pointer reorderable list. One primitive drives both the
 * page rows within a section AND the whole-section reordering, so the drag /
 * keyboard / announcement behavior is identical everywhere (PRIME-NAV-UX-001,
 * decision 9). Keyboard is the acceptance-tested path: focus a handle → Space
 * or Enter grabs → Arrow keys move → Space/Enter drops → Escape cancels, with
 * aria-live announcements throughout. Pointer drag adds the showcase drop
 * indicator + lift and is reduced-motion aware via CSS.
 */
function ReorderList({
  items,
  getKey,
  getLabel,
  onReorder,
  onAnnounce,
  lockedKeys,
  minIndex = 0,
  className,
  renderItem,
  orientationLabel = 'position',
}) {
  const [grabbedKey, setGrabbedKey] = useState(null);
  const [dragKey, setDragKey] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const handleRefs = useRef(new Map());
  const rowRefs = useRef(new Map());
  const pendingFocusKey = useRef(null);

  const locked = useCallback((key) => (lockedKeys ? lockedKeys.has(key) : false), [lockedKeys]);

  // After a keyboard move re-renders the list, keep focus on the moved handle.
  useEffect(() => {
    if (pendingFocusKey.current) {
      const el = handleRefs.current.get(pendingFocusKey.current);
      if (el) el.focus();
      pendingFocusKey.current = null;
    }
  });

  const moveBy = useCallback(
    (key, delta) => {
      const from = items.findIndex((it) => getKey(it) === key);
      if (from < 0) return;
      const to = from + delta;
      if (to < minIndex || to >= items.length) return;
      pendingFocusKey.current = key;
      onReorder(from, to);
      onAnnounce?.(
        `${getLabel(items[from])} moved to ${orientationLabel} ${to + 1} of ${items.length}.`,
      );
    },
    [items, getKey, getLabel, onReorder, onAnnounce, minIndex, orientationLabel],
  );

  const onHandleKeyDown = useCallback(
    (event, item, index) => {
      const key = getKey(item);
      const isGrabbed = grabbedKey === key;
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        if (locked(key)) {
          onAnnounce?.(`${getLabel(item)} is fixed and cannot be moved.`);
          return;
        }
        if (!isGrabbed) {
          setGrabbedKey(key);
          onAnnounce?.(
            `${getLabel(item)} grabbed, ${orientationLabel} ${index + 1} of ${items.length}. Use arrow keys to move, space to drop, escape to cancel.`,
          );
        } else {
          setGrabbedKey(null);
          onAnnounce?.(`${getLabel(item)} dropped at ${orientationLabel} ${index + 1} of ${items.length}.`);
        }
        return;
      }
      if (event.key === 'Escape' && isGrabbed) {
        event.preventDefault();
        setGrabbedKey(null);
        onAnnounce?.(`Reorder cancelled. ${getLabel(item)} returned.`);
        return;
      }
      if (!isGrabbed) return;
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        moveBy(key, -1);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        moveBy(key, 1);
      }
    },
    [grabbedKey, getKey, getLabel, locked, moveBy, onAnnounce, items.length, orientationLabel],
  );

  // ── Pointer drag (polish; no-op-safe when layout metrics are unavailable) ──
  const computeDropIndex = useCallback((clientY) => {
    let target = items.length;
    for (let i = 0; i < items.length; i += 1) {
      const el = rowRefs.current.get(getKey(items[i]));
      if (!el || typeof el.getBoundingClientRect !== 'function') continue;
      const rect = el.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      if (clientY < mid) {
        target = i;
        break;
      }
    }
    return Math.max(minIndex, target);
  }, [items, getKey, minIndex]);

  const onHandlePointerDown = useCallback(
    (event, item) => {
      const key = getKey(item);
      if (locked(key) || event.button != null && event.button !== 0) return;
      setDragKey(key);
      setGrabbedKey(null);
      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch {
        /* pointer capture unsupported */
      }
    },
    [getKey, locked],
  );

  const onHandlePointerMove = useCallback(
    (event) => {
      if (!dragKey) return;
      setDropIndex(computeDropIndex(event.clientY));
    },
    [dragKey, computeDropIndex],
  );

  const finishPointer = useCallback(() => {
    if (dragKey && dropIndex != null) {
      const from = items.findIndex((it) => getKey(it) === dragKey);
      let to = dropIndex;
      if (from >= 0 && to > from) to -= 1; // account for the removed row
      if (from >= 0 && to >= minIndex && to !== from) {
        onReorder(from, to);
        onAnnounce?.(`${getLabel(items[from])} moved to ${orientationLabel} ${to + 1}.`);
      }
    }
    setDragKey(null);
    setDropIndex(null);
  }, [dragKey, dropIndex, items, getKey, getLabel, onReorder, onAnnounce, minIndex, orientationLabel]);

  return (
    <ul className={className} role="list">
      {items.map((item, index) => {
        const key = getKey(item);
        const isGrabbed = grabbedKey === key;
        const isDragging = dragKey === key;
        return (
          <li
            key={key}
            ref={(el) => {
              if (el) rowRefs.current.set(key, el);
              else rowRefs.current.delete(key);
            }}
            className={`nme-reorder-row${isGrabbed ? ' is-grabbed' : ''}${isDragging ? ' is-dragging' : ''}${
              dragKey && dropIndex === index ? ' is-drop-before' : ''
            }`}
          >
            {renderItem(item, {
              index,
              isGrabbed,
              isDragging,
              locked: locked(key),
              handleProps: {
                ref: (el) => {
                  if (el) handleRefs.current.set(key, el);
                  else handleRefs.current.delete(key);
                },
                role: 'button',
                tabIndex: 0,
                'aria-pressed': isGrabbed,
                'aria-disabled': locked(key) || undefined,
                onKeyDown: (e) => onHandleKeyDown(e, item, index),
                onPointerDown: (e) => onHandlePointerDown(e, item),
                onPointerMove: onHandlePointerMove,
                onPointerUp: finishPointer,
                onPointerCancel: finishPointer,
              },
            })}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Curated emoji picker (AC-07). Offers ONLY the frozen catalog rows plus a
 * "None" clear. Every option is a real <button>, so the whole picker is keyboard
 * operable; Escape closes it. The client never accepts free-form emoji input.
 */
function EmojiPicker({ value, onPick, onClose, titleId }) {
  const ref = useRef(null);
  useEffect(() => {
    // Move focus into the picker so keyboard users land on the options.
    const first = ref.current?.querySelector('button');
    first?.focus();
  }, []);
  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };
  return (
    <div
      className="nme-emoji-picker"
      role="dialog"
      aria-label="Choose a group icon"
      aria-labelledby={titleId}
      ref={ref}
      onKeyDown={onKeyDown}
    >
      <div className="nme-emoji-picker-head">
        <span>Choose an icon</span>
        <button
          type="button"
          className="nme-emoji-none"
          data-testid="nme-emoji-none"
          onClick={() => onPick(null)}
        >
          None
        </button>
      </div>
      {NAV_EMOJI_ROWS.map((row) => (
        <div className="nme-emoji-row" key={`emojirow:${row.label}`}>
          <span className="nme-emoji-row-label">{row.label}</span>
          <div className="nme-emoji-row-grid">
            {row.emoji.map((emoji) => (
              <button
                type="button"
                key={`emoji:${emoji}`}
                className={`nme-emoji-option${value === emoji ? ' is-selected' : ''}`}
                data-testid={`nme-emoji-option:${emoji}`}
                aria-label={`Icon ${emoji}`}
                aria-pressed={value === emoji}
                onClick={() => onPick(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Keyboard-complete "Move to…" menu — the cross-container relocation path for
 * BOTH pointer and keyboard users (decision 1). Targets are the section bodies
 * and submenus other than the page's current container. Options are buttons, so
 * the menu is fully operable without a mouse; Escape closes it.
 */
function MoveMenu({ targets, onMove, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.querySelector('button')?.focus();
  }, []);
  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };
  return (
    <div className="nme-move-menu" role="menu" aria-label="Move to" ref={ref} onKeyDown={onKeyDown}>
      {targets.length === 0 ? (
        <div className="nme-move-empty">No other groups yet.</div>
      ) : (
        targets.map((target) => (
          <button
            type="button"
            role="menuitem"
            key={target.testKey}
            className="nme-move-to"
            data-testid={`nme-move-to:${target.testKey}`}
            onClick={() => onMove(target)}
          >
            {target.emoji ? <span aria-hidden>{target.emoji} </span> : null}
            {target.label}
          </button>
        ))
      )}
    </div>
  );
}

function MiniRailPreview({ schema, roles }) {
  const model = resolveRailModel({ schema, roles }) || [];
  const renderItem = (item, prefix) => {
    const Icon = item.icon;
    return (
      <div className="nme-preview-item" key={`${prefix}:${item.route}`}>
        {Icon ? <Icon size={14} /> : null}
        <span>{item.label}</span>
      </div>
    );
  };
  return (
    <div className="nme-preview" aria-hidden="false" aria-label="Live navigation preview">
      <div className="nme-preview-rail">
        <div className="nme-preview-brand">PRIME</div>
        {model.map((section) => (
          <div className="nme-preview-section" key={`prev:${section.id}`}>
            <div className="nme-preview-section-label">
              {section.emoji ? `${section.emoji} ` : ''}
              {section.label}
              {section.collapsed ? <span className="nme-preview-collapsed"> ▸</span> : null}
            </div>
            {section.collapsed
              ? null
              : section.items.map((item) =>
                  item.type === 'submenu' ? (
                    <div className="nme-preview-submenu" key={`prev:sub:${item.id}`}>
                      <div className="nme-preview-submenu-label">
                        {item.emoji ? `${item.emoji} ` : ''}
                        {item.label}
                        {item.collapsed ? <span className="nme-preview-collapsed"> ▸</span> : null}
                      </div>
                      {item.collapsed
                        ? null
                        : item.items.map((page) => renderItem(page, `prev:sub:${item.id}`))}
                    </div>
                  ) : (
                    renderItem(item, `prev:${section.id}`)
                  ),
                )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Build the relocation-target list for a page: every section body and submenu
// EXCEPT the page's current container. testKey doubles as a stable option id.
function buildMoveTargets(schema, route) {
  if (!isCustomLayout(schema)) return [];
  let currentKey = null;
  for (const section of schema.layout) {
    if (section.items.some((i) => i.type === 'page' && i.route === route)) currentKey = section.id;
    for (const item of section.items) {
      if (item.type === 'submenu' && item.items.some((p) => p.route === route)) {
        currentKey = `${section.id}:${item.id}`;
      }
    }
  }
  const targets = [];
  for (const section of schema.layout) {
    const sectionLabel = section.label || section.source;
    if (section.id !== currentKey) {
      targets.push({
        testKey: section.id,
        kind: 'section',
        sectionId: section.id,
        label: sectionLabel,
        emoji: section.emoji || null,
      });
    }
    for (const item of section.items) {
      if (item.type !== 'submenu') continue;
      const key = `${section.id}:${item.id}`;
      if (key === currentKey) continue;
      targets.push({
        testKey: key,
        kind: 'submenu',
        sectionId: section.id,
        submenuId: item.id,
        label: `${sectionLabel} › ${item.label || 'Group'}`,
        emoji: item.emoji || null,
      });
    }
  }
  return targets;
}

function sectionTestKey(section) {
  return section.source === 'custom' ? section.id : section.source;
}

export function NavMenuEditor() {
  const { roles } = useCurrentUser();
  // Stable identity for the role SET so the initial-load effect re-runs only
  // when the actual roles change — not on every render (a caller whose
  // useCurrentUser returns a fresh `roles` array each call must not spin the
  // load effect into an infinite setState loop).
  const rolesKey = useMemo(() => (Array.isArray(roles) ? roles.join('|') : ''), [roles]);
  const [schema, setSchema] = useState(null);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [baseRevision, setBaseRevision] = useState(null);
  const [pendingClear, setPendingClear] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [announce, setAnnounce] = useState('');

  // ── 002 transient editor UI state ──────────────────────────────────────────
  // Only one popover / inline editor is open at a time (predictable focus).
  const [emojiOpenFor, setEmojiOpenFor] = useState(null); // group id
  const [moveOpenFor, setMoveOpenFor] = useState(null); // page route
  const [rename, setRename] = useState(null); // { kind, sectionId, submenuId, id, draft, error }
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSectionDraft, setNewSectionDraft] = useState('');
  const [newSectionError, setNewSectionError] = useState(null);
  const [addSubmenuFor, setAddSubmenuFor] = useState(null); // section id
  const [submenuDraft, setSubmenuDraft] = useState('');
  const [submenuError, setSubmenuError] = useState(null);

  const hydrate = useCallback(
    (prefs) => {
      const stored = decodeNavPreferences(prefs);
      const reconciled = stored
        ? applyRbacToLayout(stored, roles, { keepEmptySections: true })
        : deriveRecommendedLayout(roles);
      setSchema(reconciled);
      setInitialSnapshot(JSON.stringify(reconciled));
      setBaseRevision(stored ? stored.revision : null);
      setPendingClear(false);
    },
    [roles],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    // Promise.resolve guards against a non-thenable return (e.g. a stubbed
    // api.get in a host-page test that never opts into the editor).
    Promise.resolve(api.get('/me/preferences'))
      .then((res) => {
        if (!active) return;
        const prefs = res?.data && typeof res.data === 'object' ? res.data : {};
        hydrate(prefs);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setLoadError(err);
        setLoading(false);
      });
    return () => {
      active = false;
    };
    // Keyed on rolesKey (a stable string), not `hydrate`/`roles` (unstable
    // identities), so the load fires once per role set. `hydrate` closes over
    // the current roles at the time the effect runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesKey]);

  const dirty = useMemo(() => {
    if (loading || !schema) return false;
    if (pendingClear && baseRevision != null) return true;
    return JSON.stringify(schema) !== initialSnapshot;
  }, [loading, schema, pendingClear, baseRevision, initialSnapshot]);

  const available = useMemo(
    () => (schema ? deriveAvailablePages({ schema, roles }) : []),
    [schema, roles],
  );
  const availableCount = available.reduce((sum, g) => sum + g.routes.length, 0);

  const applyEdit = useCallback((next) => {
    setPendingClear(false);
    setSchema(next);
  }, []);

  const onAddPage = useCallback(
    (route) => {
      applyEdit(addPage(schema, route));
      setAnnounce(`${getNavItem(route)?.label || route} added to your menu.`);
    },
    [schema, applyEdit],
  );

  const onRemovePage = useCallback(
    (route) => {
      if (isProtectedRoute(route)) return;
      applyEdit(removePage(schema, route));
      setAnnounce(`${getNavItem(route)?.label || route} removed from your menu.`);
    },
    [schema, applyEdit],
  );

  const onReorderSectionItems = useCallback(
    (sectionId, from, to) => {
      applyEdit(reorderSectionItem(schema, sectionId, from, to));
    },
    [schema, applyEdit],
  );

  const onReorderSections = useCallback(
    (from, to) => {
      applyEdit(moveSection(schema, from, to));
    },
    [schema, applyEdit],
  );

  const onReorderSubmenuPages = useCallback(
    (sectionId, submenuId, from, to) => {
      applyEdit(reorderPageWithinSubmenu(schema, sectionId, submenuId, from, to));
    },
    [schema, applyEdit],
  );

  // ── Create custom section ──────────────────────────────────────────────────
  const onConfirmNewSection = useCallback(() => {
    if (schema.layout.length >= MAX_NAV_SECTIONS) return;
    const result = validateSectionLabel(schema, null, newSectionDraft);
    if (!result.ok) {
      setNewSectionError(result.error);
      return;
    }
    applyEdit(createSection(schema, result.value));
    setAddSectionOpen(false);
    setNewSectionDraft('');
    setNewSectionError(null);
    setAnnounce(`Section "${result.value}" created.`);
  }, [schema, newSectionDraft, applyEdit]);

  // ── Rename section / submenu ───────────────────────────────────────────────
  const onRenameDraftChange = useCallback(
    (value) => {
      setRename((prev) => {
        if (!prev) return prev;
        const result =
          prev.kind === 'submenu'
            ? validateSubmenuLabel(schema, prev.sectionId, prev.submenuId, value)
            : validateSectionLabel(schema, prev.sectionId, value);
        return { ...prev, draft: value, error: result.ok ? null : result.error };
      });
    },
    [schema],
  );

  const onRenameSubmit = useCallback(() => {
    if (!rename) return;
    const result =
      rename.kind === 'submenu'
        ? validateSubmenuLabel(schema, rename.sectionId, rename.submenuId, rename.draft)
        : validateSectionLabel(schema, rename.sectionId, rename.draft);
    if (!result.ok) {
      setRename((prev) => (prev ? { ...prev, error: result.error } : prev));
      return;
    }
    applyEdit(
      rename.kind === 'submenu'
        ? renameSubmenu(schema, rename.sectionId, rename.submenuId, result.value)
        : renameSection(schema, rename.sectionId, result.value),
    );
    setRename(null);
    setAnnounce(`Renamed to "${result.value}".`);
  }, [schema, rename, applyEdit]);

  // ── Emoji ──────────────────────────────────────────────────────────────────
  const onPickSectionEmoji = useCallback(
    (sectionId, emoji) => {
      applyEdit(setSectionEmoji(schema, sectionId, emoji));
      setEmojiOpenFor(null);
      setAnnounce(emoji ? 'Section icon set.' : 'Section icon cleared.');
    },
    [schema, applyEdit],
  );

  const onPickSubmenuEmoji = useCallback(
    (sectionId, submenuId, emoji) => {
      applyEdit(setSubmenuEmoji(schema, sectionId, submenuId, emoji));
      setEmojiOpenFor(null);
      setAnnounce(emoji ? 'Submenu icon set.' : 'Submenu icon cleared.');
    },
    [schema, applyEdit],
  );

  // ── Collapse (editor edits go through the normal dirty/save flow) ───────────
  const onToggleSectionCollapseEditor = useCallback(
    (sectionId) => {
      applyEdit(toggleSectionCollapsed(schema, sectionId));
    },
    [schema, applyEdit],
  );

  const onToggleSubmenuCollapseEditor = useCallback(
    (sectionId, submenuId) => {
      applyEdit(toggleSubmenuCollapsed(schema, sectionId, submenuId));
    },
    [schema, applyEdit],
  );

  // ── Delete ───────────────────────────────────────────────────────────────
  const onDeleteSection = useCallback(
    (sectionId) => {
      applyEdit(deleteSection(schema, sectionId));
      setAnnounce('Section deleted. Its pages returned to Available.');
    },
    [schema, applyEdit],
  );

  const onDeleteSubmenu = useCallback(
    (sectionId, submenuId) => {
      applyEdit(deleteSubmenu(schema, sectionId, submenuId));
      setAnnounce('Submenu deleted. Its pages returned to Available.');
    },
    [schema, applyEdit],
  );

  // ── Submenu creation ───────────────────────────────────────────────────────
  const onConfirmNewSubmenu = useCallback(() => {
    if (!addSubmenuFor) return;
    const section = schema.layout.find((item) => item.id === addSubmenuFor);
    if (!section || section.items.length >= MAX_NAV_SECTION_ITEMS) return;
    const result = validateSubmenuLabel(schema, addSubmenuFor, null, submenuDraft);
    if (!result.ok) {
      setSubmenuError(result.error);
      return;
    }
    applyEdit(createSubmenu(schema, addSubmenuFor, result.value));
    setAddSubmenuFor(null);
    setSubmenuDraft('');
    setSubmenuError(null);
    setAnnounce(`Submenu "${result.value}" created.`);
  }, [schema, addSubmenuFor, submenuDraft, applyEdit]);

  // ── Move a page between groups ─────────────────────────────────────────────
  const onMovePage = useCallback(
    (route, target) => {
      applyEdit(movePage(schema, route, target));
      setMoveOpenFor(null);
      setAnnounce(`${getNavItem(route)?.label || route} moved to ${target.label}.`);
    },
    [schema, applyEdit],
  );

  const onSelectAll = useCallback(() => {
    applyEdit(selectAll(schema, roles));
    setAnnounce('All available pages added to your menu.');
  }, [schema, roles, applyEdit]);

  const onClearOptional = useCallback(() => {
    applyEdit(clearOptional(schema));
    setAnnounce('Optional pages cleared. Only Mission Brief remains.');
  }, [schema, applyEdit]);

  const onResetRecommended = useCallback(() => {
    const recommended = deriveRecommendedLayout(roles);
    setSchema(recommended);
    setPendingClear(true);
    setAnnounce('Menu reset to the recommended layout.');
  }, [roles]);

  const onDiscard = useCallback(() => {
    setSchema(initialSnapshot ? JSON.parse(initialSnapshot) : null);
    setPendingClear(false);
    setConflict(null);
    setAnnounce('Unsaved changes discarded.');
  }, [initialSnapshot]);

  const persist = useCallback(
    async ({ overrideBaseRevision } = {}) => {
      const base = overrideBaseRevision !== undefined ? overrideBaseRevision : baseRevision;
      // Reset-to-recommendation clears the custom layout entirely → default rail.
      if (pendingClear) {
        await api.patch('/me/preferences', encodeNavPreferences(null));
        const recommended = deriveRecommendedLayout(roles);
        setSchema(recommended);
        setInitialSnapshot(JSON.stringify(recommended));
        setBaseRevision(null);
        setPendingClear(false);
        return;
      }
      const toSave = withBumpedRevision({ ...schema, revision: base ?? 0 });
      await api.patch('/me/preferences', encodeNavPreferences(toSave));
      setSchema(toSave);
      setInitialSnapshot(JSON.stringify(toSave));
      setBaseRevision(toSave.revision);
    },
    [schema, baseRevision, pendingClear, roles],
  );

  const refreshConflict = useCallback(async () => {
    const fresh = await api.get('/me/preferences');
    const prefs = fresh?.data && typeof fresh.data === 'object' ? fresh.data : {};
    setConflict({ prefs, serverRevision: readRevision(prefs) });
    setAnnounce('Your menu was changed elsewhere. Choose how to resolve the conflict.');
  }, []);

  const onSave = useCallback(async () => {
    if (saving || !schema) return;
    setSaving(true);
    try {
      // Conflict check: compare our loaded revision against the server's current
      // stored revision via a fresh GET before writing (decision 7).
      const fresh = await api.get('/me/preferences');
      const freshPrefs = fresh?.data && typeof fresh.data === 'object' ? fresh.data : {};
      const serverRevision = readRevision(freshPrefs);
      if (serverRevision !== baseRevision) {
        setConflict({ prefs: freshPrefs, serverRevision });
        setAnnounce('Your menu was changed elsewhere. Choose how to resolve the conflict.');
        setSaving(false);
        return;
      }
      await persist();
      broadcastNavChange();
      toast.success({ title: 'Menu saved', body: 'Your personal navigation is updated.' });
    } catch (err) {
      if (isNavigationRevisionConflict(err)) {
        try {
          await refreshConflict();
        } catch (refreshError) {
          toast.error({ title: 'Could not save menu', body: refreshError.message });
        }
      } else {
        toast.error({ title: 'Could not save menu', body: err.message });
      }
    } finally {
      setSaving(false);
    }
  }, [saving, schema, baseRevision, persist, refreshConflict]);

  const onConflictReload = useCallback(() => {
    if (!conflict) return;
    hydrate(conflict.prefs);
    setConflict(null);
    setAnnounce('Reloaded the menu saved elsewhere.');
  }, [conflict, hydrate]);

  const onConflictOverwrite = useCallback(async () => {
    if (!conflict || saving) return;
    setSaving(true);
    try {
      const fresh = await api.get('/me/preferences');
      const freshPrefs = fresh?.data && typeof fresh.data === 'object' ? fresh.data : {};
      await persist({ overrideBaseRevision: readRevision(freshPrefs) });
      broadcastNavChange();
      setConflict(null);
      toast.success({ title: 'Menu saved', body: 'Your version replaced the other changes.' });
    } catch (err) {
      if (isNavigationRevisionConflict(err)) {
        try {
          await refreshConflict();
        } catch (refreshError) {
          toast.error({ title: 'Could not save menu', body: refreshError.message });
        }
      } else {
        toast.error({ title: 'Could not save menu', body: err.message });
      }
    } finally {
      setSaving(false);
    }
  }, [conflict, saving, persist, refreshConflict]);

  if (loading) {
    return (
      <section className="nme" aria-busy="true" id="personal-navigation">
        <div className="nme-loading">Loading your menu…</div>
      </section>
    );
  }
  if (loadError || !schema) {
    return (
      <section className="nme" id="personal-navigation">
        <p className="nme-error" role="alert">
          Could not load your navigation preferences. Reload the page to try again.
        </p>
      </section>
    );
  }

  const sections = isCustomLayout(schema) ? schema.layout : [];
  const pageCount = sections.reduce(
    (n, s) =>
      n +
      s.items.reduce((m, i) => m + (i.type === 'submenu' ? i.items.length : 1), 0),
    0,
  );

  // A single page row — used inside both section bodies and submenus. Non-Home
  // pages carry the keyboard-complete "Move to…" menu and a remove control.
  const renderPageRow = (item, { pageHandle, locked }) => {
    const meta = getNavItem(item.route);
    const Icon = meta?.icon;
    const protectedRoute = isProtectedRoute(item.route);
    const moveOpen = moveOpenFor === item.route;
    return (
      <div
        className={`nme-page${protectedRoute ? ' is-protected' : ''}`}
        data-testid={`nme-page:${item.route}`}
      >
        <span
          {...pageHandle}
          className="nme-drag-handle"
          aria-label={locked ? `${meta?.label || item.route} is fixed` : `Reorder ${meta?.label || item.route}`}
          title={locked ? 'Mission Brief is fixed' : 'Drag or press Space to reorder'}
        >
          <IconTarget size={12} />
        </span>
        {Icon ? <Icon size={16} /> : null}
        <span className="nme-page-label">{meta?.label || item.route}</span>
        {protectedRoute ? (
          <span className="nme-pill" title="Always in your menu">
            Fixed
          </span>
        ) : (
          <>
            <div className="nme-move">
              <button
                type="button"
                className="nme-move-trigger"
                data-testid={`nme-move-trigger:${item.route}`}
                aria-haspopup="menu"
                aria-expanded={moveOpen}
                onClick={() => setMoveOpenFor((cur) => (cur === item.route ? null : item.route))}
                title="Move to another group"
              >
                Move
              </button>
              {moveOpen ? (
                <MoveMenu
                  targets={buildMoveTargets(schema, item.route)}
                  onMove={(target) => onMovePage(item.route, target)}
                  onClose={() => setMoveOpenFor(null)}
                />
              ) : null}
            </div>
            <button
              type="button"
              className="nme-remove"
              onClick={() => onRemovePage(item.route)}
              aria-label={`Remove ${meta?.label || item.route} from your menu`}
              data-testid={`nme-remove:${item.route}`}
            >
              <IconClose size={14} />
            </button>
          </>
        )}
      </div>
    );
  };

  // Inline rename editor (shared by sections + submenus; one open at a time).
  const renderRenameEditor = () => (
    <span className="nme-rename">
      <input
        className="nme-rename-input"
        data-testid="nme-rename-input"
        value={rename.draft}
        aria-label="Group name"
        aria-invalid={Boolean(rename.error)}
        autoFocus
        onChange={(e) => onRenameDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onRenameSubmit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setRename(null);
          }
        }}
      />
      <button
        type="button"
        className="nme-icon-btn"
        data-testid="nme-rename-save"
        aria-label="Save name"
        onClick={onRenameSubmit}
      >
        <IconCheck size={14} />
      </button>
      <button
        type="button"
        className="nme-icon-btn"
        data-testid="nme-rename-cancel"
        aria-label="Cancel rename"
        onClick={() => setRename(null)}
      >
        <IconClose size={14} />
      </button>
      {rename.error ? (
        <span className="nme-rename-error" role="alert" data-testid="nme-rename-error">
          {rename.error}
        </span>
      ) : null}
    </span>
  );

  const renderSubmenuCard = (section, submenu, { itemHandle, locked = false } = {}) => {
    const isRenaming = rename && rename.kind === 'submenu' && rename.submenuId === submenu.id;
    return (
      <div
        className={`nme-submenu${submenu.collapsed ? ' is-collapsed' : ''}`}
        data-testid={`nme-submenu:${submenu.id}`}
        key={`sub:${submenu.id}`}
      >
        <div className="nme-submenu-head">
          {itemHandle ? (
            <span
              {...itemHandle}
              className="nme-drag-handle"
              aria-label={locked ? `${submenu.label || 'Submenu'} is fixed` : `Reorder ${submenu.label || 'submenu'}`}
              title="Drag or press Space to reorder"
            >
              <IconTarget size={12} />
            </span>
          ) : null}
          <button
            type="button"
            className={`nme-collapse-btn${submenu.collapsed ? ' is-collapsed' : ''}`}
            data-testid={`nme-collapse-submenu:${submenu.id}`}
            aria-expanded={!submenu.collapsed}
            aria-label={submenu.collapsed ? `Expand ${submenu.label || 'submenu'}` : `Collapse ${submenu.label || 'submenu'}`}
            onClick={() => onToggleSubmenuCollapseEditor(section.id, submenu.id)}
          >
            <IconChevron size={12} className="nme-collapse-chevron" />
          </button>
          <div className="nme-emoji-control">
            <button
              type="button"
              className="nme-emoji-trigger"
              data-testid={`nme-emoji-trigger:${submenu.id}`}
              aria-haspopup="dialog"
              aria-expanded={emojiOpenFor === submenu.id}
              onClick={() => setEmojiOpenFor((cur) => (cur === submenu.id ? null : submenu.id))}
              title="Choose a submenu icon"
            >
              {submenu.emoji ? <span aria-hidden>{submenu.emoji}</span> : <IconSparkles size={12} />}
            </button>
            {emojiOpenFor === submenu.id ? (
              <EmojiPicker
                value={submenu.emoji}
                onPick={(emoji) => onPickSubmenuEmoji(section.id, submenu.id, emoji)}
                onClose={() => setEmojiOpenFor(null)}
              />
            ) : null}
          </div>
          {isRenaming ? (
            renderRenameEditor()
          ) : (
            <span className="nme-submenu-title">{submenu.label || 'Group'}</span>
          )}
          <span className="nme-submenu-actions">
            <button
              type="button"
              className="nme-icon-btn"
              data-testid={`nme-rename-submenu:${submenu.id}`}
              aria-label={`Rename ${submenu.label || 'submenu'}`}
              onClick={() =>
                setRename({
                  kind: 'submenu',
                  sectionId: section.id,
                  submenuId: submenu.id,
                  id: submenu.id,
                  draft: submenu.label || '',
                  error: null,
                })
              }
            >
              Rename
            </button>
            <button
              type="button"
              className="nme-icon-btn nme-danger"
              data-testid={`nme-delete-submenu:${submenu.id}`}
              aria-label={`Delete ${submenu.label || 'submenu'}`}
              onClick={() => onDeleteSubmenu(section.id, submenu.id)}
            >
              <IconClose size={12} />
            </button>
          </span>
        </div>
        {submenu.collapsed ? null : (
          <ReorderList
            className="nme-page-list nme-submenu-pages"
            items={submenu.items}
            getKey={(it) => it.route}
            getLabel={(it) => getNavItem(it.route)?.label || it.route}
            onReorder={(from, to) => onReorderSubmenuPages(section.id, submenu.id, from, to)}
            onAnnounce={setAnnounce}
            orientationLabel="position"
            renderItem={(item, { handleProps: pageHandle, locked }) =>
              renderPageRow(item, { pageHandle, locked })
            }
          />
        )}
      </div>
    );
  };

  const renderSectionCard = (section, { handleProps }) => {
    const isHome = section.source === HOME_SECTION_SOURCE;
    const homePinned = isHome;
    const isRenaming = rename && rename.kind === 'section' && rename.sectionId === section.id;
    const submenuCapReached = section.items.length >= MAX_NAV_SECTION_ITEMS;
    const displayName = section.label || section.source;
    return (
      <div
        className={`nme-section${section.collapsed ? ' is-collapsed' : ''}`}
        data-testid={`nme-section:${sectionTestKey(section)}`}
      >
        <div className="nme-section-head">
          <span
            {...handleProps}
            className="nme-drag-handle nme-section-handle"
            aria-label={`Reorder section ${displayName}`}
            title="Drag or press Space to reorder this section"
          >
            <IconChevron size={12} />
            <IconChevron size={12} />
          </span>
          {!isHome ? (
            <button
              type="button"
              className={`nme-collapse-btn${section.collapsed ? ' is-collapsed' : ''}`}
              data-testid={`nme-collapse-section:${section.id}`}
              aria-expanded={!section.collapsed}
              aria-label={section.collapsed ? `Expand ${displayName}` : `Collapse ${displayName}`}
              onClick={() => onToggleSectionCollapseEditor(section.id)}
            >
              <IconChevron size={14} className="nme-collapse-chevron" />
            </button>
          ) : null}
          {!isHome ? (
            <div className="nme-emoji-control">
              <button
                type="button"
                className="nme-emoji-trigger"
                data-testid={`nme-emoji-trigger:${section.id}`}
                aria-haspopup="dialog"
                aria-expanded={emojiOpenFor === section.id}
                onClick={() => setEmojiOpenFor((cur) => (cur === section.id ? null : section.id))}
                title="Choose a section icon"
              >
                {section.emoji ? <span aria-hidden>{section.emoji}</span> : <IconSparkles size={14} />}
              </button>
              {emojiOpenFor === section.id ? (
                <EmojiPicker
                  value={section.emoji}
                  onPick={(emoji) => onPickSectionEmoji(section.id, emoji)}
                  onClose={() => setEmojiOpenFor(null)}
                />
              ) : null}
            </div>
          ) : null}
          {isRenaming ? (
            renderRenameEditor()
          ) : (
            <span className="nme-section-title">{displayName}</span>
          )}
          <span className="nme-section-actions">
            {!isHome ? (
              <button
                type="button"
                className="nme-icon-btn"
                data-testid={`nme-rename-section:${section.id}`}
                aria-label={`Rename ${displayName}`}
                onClick={() =>
                  setRename({
                    kind: 'section',
                    sectionId: section.id,
                    submenuId: null,
                    id: section.id,
                    draft: displayName,
                    error: null,
                  })
                }
              >
                Rename
              </button>
            ) : null}
            <button
              type="button"
              className="nme-icon-btn"
              data-testid={`nme-add-submenu:${section.id}`}
              disabled={submenuCapReached}
              title={
                submenuCapReached
                  ? `A section can contain at most ${MAX_NAV_SECTION_ITEMS} items`
                  : 'Add a submenu'
              }
              aria-label={
                submenuCapReached
                  ? `Add submenu unavailable: maximum ${MAX_NAV_SECTION_ITEMS} section items reached`
                  : `Add submenu to ${displayName}`
              }
              onClick={() => {
                setAddSubmenuFor(section.id);
                setSubmenuDraft('');
                setSubmenuError(null);
              }}
            >
              + Submenu
            </button>
            {section.source === 'custom' ? (
              <button
                type="button"
                className="nme-icon-btn nme-danger"
                data-testid={`nme-delete-section:${section.id}`}
                aria-label={`Delete ${displayName}`}
                onClick={() => onDeleteSection(section.id)}
              >
                <IconClose size={14} />
              </button>
            ) : null}
          </span>
        </div>
        {addSubmenuFor === section.id ? (
          <div className="nme-add-submenu-form">
            <input
              className="nme-submenu-input"
              data-testid="nme-submenu-input"
              value={submenuDraft}
              aria-label="Submenu name"
              aria-invalid={Boolean(submenuError)}
              autoFocus
              onChange={(e) => {
                setSubmenuDraft(e.target.value);
                setSubmenuError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onConfirmNewSubmenu();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setAddSubmenuFor(null);
                }
              }}
            />
            <button
              type="button"
              className="btn ghost"
              data-testid="nme-submenu-confirm"
              onClick={onConfirmNewSubmenu}
            >
              Add submenu
            </button>
            <button
              type="button"
              className="nme-icon-btn"
              aria-label="Cancel"
              onClick={() => setAddSubmenuFor(null)}
            >
              <IconClose size={14} />
            </button>
            {submenuError ? (
              <span className="nme-inline-error" role="alert">
                {submenuError}
              </span>
            ) : null}
          </div>
        ) : null}
        {section.collapsed ? null : (
          <ReorderList
            className="nme-page-list"
            items={section.items}
            getKey={(item) =>
              item.type === 'submenu' ? `submenu:${item.id}` : `page:${item.route}`
            }
            getLabel={(item) =>
              item.type === 'submenu'
                ? item.label || 'Submenu'
                : getNavItem(item.route)?.label || item.route
            }
            onReorder={(from, to) => onReorderSectionItems(section.id, from, to)}
            onAnnounce={setAnnounce}
            minIndex={homePinned ? 1 : 0}
            lockedKeys={homePinned ? new Set(['page:/home']) : undefined}
            orientationLabel="position"
            renderItem={(item, { handleProps: itemHandle, locked }) =>
              item.type === 'submenu'
                ? renderSubmenuCard(section, item, { itemHandle, locked })
                : renderPageRow(item, { pageHandle: itemHandle, locked })
            }
          />
        )}
      </div>
    );
  };

  return (
    <section className="nme" id="personal-navigation" data-testid="nav-menu-editor">
      <div aria-live="polite" className="nme-sr-only" data-testid="nme-live">
        {announce}
      </div>

      <header className="nme-header">
        <div>
          <span className="eyebrow">
            <IconSparkles size={14} /> Personal navigation
          </span>
          <h2 className="card-title">Customize your menu</h2>
          <p className="card-subtitle">
            Pick the pages you use and order them by priority. Reorder by dragging a handle or with
            the keyboard: focus a handle, press Space to grab, arrow keys to move, Space to drop.
            Everything stays inside what your role is authorized to see.
          </p>
        </div>
        <div className="nme-toolbar" role="group" aria-label="Menu actions">
          <button type="button" className="btn ghost" onClick={onSelectAll} data-testid="nme-select-all">
            Select all
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={onClearOptional}
            data-testid="nme-clear-optional"
          >
            Clear optional
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={onResetRecommended}
            data-testid="nme-reset"
          >
            Reset to recommended
          </button>
        </div>
      </header>

      {conflict ? (
        <div className="nme-conflict" role="alert" data-testid="nme-conflict">
          <p>
            <strong>Your menu changed somewhere else.</strong> Reload the other version, or overwrite
            it with what you have here.
          </p>
          <div className="nme-conflict-actions">
            <button type="button" className="btn ghost" onClick={onConflictReload} data-testid="nme-conflict-reload">
              Reload their changes
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={onConflictOverwrite}
              disabled={saving}
              data-testid="nme-conflict-overwrite"
            >
              Overwrite with mine
            </button>
          </div>
        </div>
      ) : null}

      <div className="nme-grid">
        {/* My menu — the editable layout */}
        <div className="nme-col nme-mymenu" data-testid="nme-mymenu">
          <div className="nme-col-head">
            <h3>My menu</h3>
            <span className="nme-count">{pageCount} pages</span>
          </div>
          <ReorderList
            className="nme-section-list"
            items={sections}
            getKey={(s) => s.id}
            getLabel={(s) => s.label || s.source}
            onReorder={onReorderSections}
            onAnnounce={setAnnounce}
            orientationLabel="section"
            renderItem={(section, { handleProps }) => renderSectionCard(section, { handleProps })}
          />

          {/* Create a personal section (Benji's "dumpster fire"). */}
          <div className="nme-add-section">
            {addSectionOpen ? (
              <div className="nme-add-section-form">
                <input
                  className="nme-new-section-input"
                  data-testid="nme-new-section-input"
                  value={newSectionDraft}
                  aria-label="New section name"
                  aria-invalid={Boolean(newSectionError)}
                  autoFocus
                  onChange={(e) => {
                    setNewSectionDraft(e.target.value);
                    setNewSectionError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onConfirmNewSection();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setAddSectionOpen(false);
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn primary"
                  data-testid="nme-new-section-confirm"
                  onClick={onConfirmNewSection}
                >
                  Create
                </button>
                <button
                  type="button"
                  className="nme-icon-btn"
                  aria-label="Cancel new section"
                  onClick={() => setAddSectionOpen(false)}
                >
                  <IconClose size={14} />
                </button>
                {newSectionError ? (
                  <span
                    className="nme-inline-error"
                    role="alert"
                    data-testid="nme-new-section-error"
                  >
                    {newSectionError}
                  </span>
                ) : null}
              </div>
            ) : (
              <button
                type="button"
                className="btn ghost nme-add-section-trigger"
                data-testid="nme-add-section"
                disabled={sections.length >= MAX_NAV_SECTIONS}
                title={
                  sections.length >= MAX_NAV_SECTIONS
                    ? `A menu can contain at most ${MAX_NAV_SECTIONS} sections`
                    : 'Add a section'
                }
                aria-label={
                  sections.length >= MAX_NAV_SECTIONS
                    ? `Add section unavailable: maximum ${MAX_NAV_SECTIONS} sections reached`
                    : 'Add section'
                }
                onClick={() => {
                  setAddSectionOpen(true);
                  setNewSectionDraft('');
                  setNewSectionError(null);
                }}
              >
                + Add section
              </button>
            )}
          </div>
        </div>

        {/* Available pages — the add source */}
        <div className="nme-col nme-available" data-testid="nme-available">
          <div className="nme-col-head">
            <h3>Available pages</h3>
            <span className="nme-count">{availableCount} available</span>
          </div>
          {availableCount === 0 ? (
            <p className="nme-empty">Every page you can access is already in your menu.</p>
          ) : (
            available.map((group) => (
              <div className="nme-available-group" key={`avail:${group.key}`}>
                <div className="nme-available-group-label">{group.label}</div>
                <ul className="nme-available-list" role="list">
                  {group.routes.map((route) => {
                    const Icon = route.icon;
                    return (
                      <li key={`avail:${group.key}:${route.route}`}>
                        <button
                          type="button"
                          className="nme-add"
                          onClick={() => onAddPage(route.route)}
                          data-testid={`nme-add:${route.route}`}
                        >
                          {Icon ? <Icon size={16} /> : null}
                          <span className="nme-page-label">{route.label}</span>
                          <span className="nme-add-plus" aria-hidden>
                            +
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

        {/* Live rail preview — the showcase centerpiece */}
        <div className="nme-col nme-previewcol">
          <div className="nme-col-head">
            <h3>Live preview</h3>
          </div>
          <MiniRailPreview schema={schema} roles={roles} />
        </div>
      </div>

      {/* Dirty bar */}
      <div className={`nme-dirtybar${dirty ? ' is-dirty' : ''}`} data-testid="nme-dirtybar">
        <span className="nme-dirty-cue">
          {saving ? 'Saving menu…' : dirty ? 'Unsaved menu changes' : 'Menu up to date'}
        </span>
        <div className="nme-dirty-actions">
          <button
            type="button"
            className="btn ghost"
            onClick={onDiscard}
            disabled={!dirty || saving}
            data-testid="nme-discard"
          >
            Discard
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={onSave}
            disabled={!dirty || saving}
            data-testid="nme-save"
          >
            <IconCheck size={16} /> Save menu
          </button>
        </div>
      </div>
    </section>
  );
}

export default NavMenuEditor;
